/**
 * Outline Proposal Store — Layer 2 (per-document outline / question proposals).
 *
 * Flow: start → poll until complete → user edits locally (titles, word
 * counts, notes, delete/reorder) → accept (writes the outline) | discard.
 * Spec: Specs/response-structure-proposal-spec.md §3.
 */

import { create } from 'zustand'
import {
  startOutlineProposal,
  getOutlineProposal,
  acceptOutlineProposal,
  discardOutlineProposal,
  type OutlineGeneration,
  type OutlineProposal,
  type QuestionsProposal,
  type ProposedSection,
  type ProposedQuestion,
  type OutlineAcceptWarning,
} from '@/lib/api'
import { useDraftingStore } from '@/stores/drafting-store'

const POLL_INTERVAL_MS = 2000
const MAX_POLL_ATTEMPTS = 90

/**
 * What the user reads when a proposal fails. The one home for this copy
 * (approved by Kapil, 2026-10-09).
 *
 * Picked by the generation's `failureReason`, never by its stored `error`,
 * which is diagnostic text for logs.
 */
export const PROPOSAL_FAILURE_COPY = {
  /** The AI stopped at its output cap. Only outline proposals report this. */
  outputLimit:
    'The AI ran out of room before it finished this outline, so nothing was proposed. Trying again may stop at the same point. You can add sections by hand instead.',
  /** The run gave up at its deadline, was found stuck, or the poll ran out. */
  timedOut: 'This took too long and was stopped before anything was proposed. Try again in a moment.',
  /** Every other failure. */
  generic: 'Something went wrong and nothing was proposed. Try again.',
} as const

function failureMessage(generation: OutlineGeneration): string {
  if (generation.failureReason === 'output_limit') return PROPOSAL_FAILURE_COPY.outputLimit
  if (generation.failureReason === 'timed_out') return PROPOSAL_FAILURE_COPY.timedOut
  return PROPOSAL_FAILURE_COPY.generic
}

/** Shown when the held proposal and the target artifact disagree. */
const FOREIGN_PROPOSAL_ERROR =
  'This proposal belongs to another document — reload and try again'

/**
 * The outline CAS is mandatory. Accepting without `expectedOutlineVersion`
 * turns the guard off entirely: the handler's `status !== 'complete'` check is
 * TOCTOU, so two concurrent accepts of the same generation would both append.
 * Fail loudly rather than write unguarded.
 */
const MISSING_OUTLINE_VERSION_ERROR =
  'Couldn’t confirm the current outline — reload the document and try again'

/**
 * A blank title must never reach the accept. The backend's `title.min(1)` has no
 * `.trim()`, so a whitespace-only title validates and persists as an unnamed
 * section; an empty one fails as a blanket 400 that loses the WHOLE review
 * rather than the single row. Gate it here (spec §2.4).
 */
const UNTITLED_SECTION_ERROR =
  'Give every section a title before accepting — one is still untitled'

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export interface EditableSection extends ProposedSection {
  /** User-authored notes added during review (section brief). */
  userNotes: string
  /**
   * Where the row came from: `ai` for a model-proposed row, `manual` for one the
   * reviewer added by hand during review. Carried through to the accept payload
   * — the backend's `sectionsAdded` telemetry counts the manual ones, and used
   * to be structurally zero because the payload hardcoded `'ai'`.
   *
   * ⚠ Not part of `ProposedSection`: the server proposal has no such field, so
   * `hydrate()` defaults every model row to `'ai'`.
   */
  source: 'ai' | 'manual'
}

interface OutlineProposalState {
  /** Artifact the current state belongs to (stores are global). */
  artifactId: string | null
  generation: OutlineGeneration | null
  sections: EditableSection[]
  questions: ProposedQuestion[]
  isGenerating: boolean
  isAccepting: boolean
  error: string | null
  /**
   * True once the user has edited the held proposal (rename, word count,
   * notes, delete, reorder). Regenerate throws those edits away, so the UI
   * confirms first. Cleared whenever the proposal is replaced or resolved.
   */
  isDirty: boolean
  /**
   * Warnings the last successful accept returned (same-title collisions the
   * reviewer accepted anyway, or ones the client-side check missed). Kept
   * rather than discarded so the area can show them after the proposal clears.
   */
  acceptWarnings: OutlineAcceptWarning[]
  /** Sections the last successful accept appended. 0 until one lands. */
  lastAddedSectionCount: number

  start: (artifactId: string) => Promise<void>
  load: (artifactId: string) => Promise<void>
  /**
   * Append a blank, editable row the reviewer authors by hand. Returns the
   * minted key (so the caller can focus the new row), or null if the held
   * proposal isn't an outline.
   */
  addSection: () => string | null
  updateSection: (
    key: string,
    patch: Partial<Pick<EditableSection, 'title' | 'suggestedWordCount' | 'userNotes'>>
  ) => void
  removeSection: (key: string) => void
  moveSection: (key: string, direction: -1 | 1) => void
  removeQuestion: (key: string) => void
  /** Accept the edited proposal → writes the outline. Returns true on success. */
  accept: (artifactId: string) => Promise<boolean>
  discard: (artifactId: string) => Promise<void>
  reset: () => void
}

/**
 * True when nothing is generating, failed or awaiting review — the state in
 * which `OutlineProposalArea` falls through to its idle branch. The additions
 * trigger outside the area keys off this so the two never disagree about
 * whether "Propose more sections" is on offer.
 */
export function selectIsProposalIdle(
  state: Pick<OutlineProposalState, 'isGenerating' | 'generation'>
): boolean {
  if (state.isGenerating) return false
  const status = state.generation?.status
  return status !== 'failed' && status !== 'complete'
}

/**
 * Bumped by every start(), load() and reset(). A continuation whose captured
 * epoch is stale abandons silently and never writes, so a poll for an artifact
 * the user has navigated away from can't clobber the live one.
 */
let pollEpoch = 0

function hydrate(generation: OutlineGeneration | null): {
  sections: EditableSection[]
  questions: ProposedQuestion[]
} {
  if (!generation?.proposal || generation.status !== 'complete') {
    return { sections: [], questions: [] }
  }
  if (generation.kind === 'outline') {
    const proposal = generation.proposal as OutlineProposal
    return {
      sections: proposal.sections.map((s) => ({ ...s, userNotes: '', source: 'ai' as const })),
      questions: [],
    }
  }
  const proposal = generation.proposal as QuestionsProposal
  return { sections: [], questions: proposal.questions }
}

async function pollUntilTerminal(
  artifactId: string,
  epoch: number,
  set: (partial: Partial<OutlineProposalState>) => void,
  get: () => OutlineProposalState
): Promise<void> {
  for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
    await sleep(POLL_INTERVAL_MS)
    if (epoch !== pollEpoch) return
    const generation = await getOutlineProposal(artifactId).catch(() => null)
    if (epoch !== pollEpoch) return
    if (!generation) continue
    // Never write a proposal that belongs to another artifact. The epoch check
    // above covers the store moving on; this covers a response that doesn't
    // match what we asked for. Keep polling rather than writing it.
    if (generation.artifactId !== artifactId || get().artifactId !== artifactId) continue
    if (generation.status === 'complete') {
      set({ generation, ...hydrate(generation), isGenerating: false, isDirty: false })
      return
    }
    if (generation.status === 'failed') {
      set({ generation, isGenerating: false, error: failureMessage(generation) })
      return
    }
  }
  if (epoch !== pollEpoch) return
  set({ isGenerating: false, error: PROPOSAL_FAILURE_COPY.timedOut })
}

export const useOutlineProposalStore = create<OutlineProposalState>((set, get) => ({
  artifactId: null,
  generation: null,
  sections: [],
  questions: [],
  isGenerating: false,
  isAccepting: false,
  error: null,
  isDirty: false,
  acceptWarnings: [],
  lastAddedSectionCount: 0,

  start: async (artifactId) => {
    // In-flight guard: without it an impatient repeat click dispatches a second
    // worker that supersedes the first.
    if (get().isGenerating) return
    // Orphan any poll still running for a previous artifact/generation.
    const epoch = ++pollEpoch
    set({
      artifactId,
      isGenerating: true,
      error: null,
      generation: null,
      sections: [],
      questions: [],
      isDirty: false,
      acceptWarnings: [],
      lastAddedSectionCount: 0,
    })
    try {
      await startOutlineProposal(artifactId)
    } catch (err) {
      if (epoch !== pollEpoch) return
      set({ isGenerating: false, error: (err as Error).message })
      return
    }
    if (epoch !== pollEpoch) return
    await pollUntilTerminal(artifactId, epoch, set, get)
  },

  load: async (artifactId) => {
    // ⚠ Never re-hydrate over unsaved review edits (spec §2.5).
    // `OutlineProposalArea` calls load() on every mount, so any remount — a tab
    // switch is enough — used to refetch the server proposal and reset isDirty,
    // wiping every local edit. That was already wrong for a tweak to a model
    // row; with hand-authored rows it silently destroys something the reviewer
    // typed from scratch. Returns BEFORE bumping the epoch so an (unreachable,
    // but cheap to respect) in-flight poll isn't orphaned for nothing.
    if (get().artifactId === artifactId && get().isDirty) return

    // This load owns the state from here on; any in-flight poll is superseded.
    const epoch = ++pollEpoch
    // Switching artifacts clears stale state from a previous document —
    // including spinners left behind by the poll or accept we just orphaned.
    if (get().artifactId !== artifactId) {
      set({
        artifactId,
        generation: null,
        sections: [],
        questions: [],
        isGenerating: false,
        isAccepting: false,
        error: null,
        isDirty: false,
        acceptWarnings: [],
        lastAddedSectionCount: 0,
      })
    }
    try {
      const generation = await getOutlineProposal(artifactId)
      if (epoch !== pollEpoch) return
      if (!generation || generation.status === 'accepted' || generation.status === 'superseded') {
        set({ generation: null, sections: [], questions: [], isGenerating: false, isDirty: false })
        return
      }
      if (generation.status === 'pending' || generation.status === 'running') {
        set({ generation, isGenerating: true })
        await pollUntilTerminal(artifactId, epoch, set, get)
        return
      }
      if (generation.status === 'failed') {
        set({ generation, isGenerating: false, error: failureMessage(generation) })
        return
      }
      set({ generation, ...hydrate(generation), isGenerating: false, isDirty: false })
    } catch (err) {
      if (epoch !== pollEpoch) return
      set({ error: (err as Error).message })
    }
  },

  addSection: () => {
    // Questions are extracted word-for-word and verified against the source —
    // authoring one by hand is a different feature (spec §3).
    if (get().generation?.kind !== 'outline') return null

    // ⚠ NOT the model's positional `sec-${i+1}` scheme (`outline-generation.ts`).
    // Delete `sec-2`, add a manual row, and a positional key would mint a second
    // `sec-2`: `updateSection` / `removeSection` match on `s.key === key` and
    // would hit BOTH rows, and the backend's `computeOutlineEditStats` would
    // false-match the manual row against the proposal row it shadows and count
    // it as a rename. `manual-<uuid>` is 43 chars — inside the schema's
    // `key.max(64)`.
    const key = `manual-${crypto.randomUUID()}`
    set({
      sections: [
        ...get().sections,
        {
          key,
          title: '',
          suggestedWordCount: null,
          citations: [],
          mappedCriteriaIds: [],
          mappedComplianceIds: [],
          themeIds: [],
          guidance: '',
          userNotes: '',
          source: 'manual',
        },
      ],
      // Every sibling mutator marks dirty; without it Regenerate would discard
      // the hand-authored row with no confirmation.
      isDirty: true,
    })
    return key
  },

  updateSection: (key, patch) => {
    set({
      sections: get().sections.map((s) => (s.key === key ? { ...s, ...patch } : s)),
      isDirty: true,
    })
  },

  removeSection: (key) => {
    set({ sections: get().sections.filter((s) => s.key !== key), isDirty: true })
  },

  moveSection: (key, direction) => {
    const sections = [...get().sections]
    const index = sections.findIndex((s) => s.key === key)
    const target = index + direction
    if (index < 0 || target < 0 || target >= sections.length) return
    const [moved] = sections.splice(index, 1)
    sections.splice(target, 0, moved!)
    set({ sections, isDirty: true })
  },

  removeQuestion: (key) => {
    set({ questions: get().questions.filter((q) => q.key !== key), isDirty: true })
  },

  accept: async (artifactId) => {
    const { generation, sections, questions, artifactId: currentArtifactId } = get()
    if (!generation) return false

    // Cross-artifact guard: a proposal left over from another document must
    // never be sent to this artifact's accept endpoint.
    if (generation.artifactId !== artifactId || currentArtifactId !== artifactId) {
      set({ error: FOREIGN_PROPOSAL_ERROR })
      return false
    }

    const payload =
      generation.kind === 'outline'
        ? {
            sections: sections.map((s) => ({
              key: s.key,
              title: s.title,
              suggestedWordCount: s.suggestedWordCount,
              mappedCriteriaIds: s.mappedCriteriaIds,
              mappedComplianceIds: s.mappedComplianceIds,
              themeIds: s.themeIds,
              guidance: s.guidance,
              userNotes: s.userNotes,
              citations: s.citations,
              // Each row's OWN source. This was hardcoded 'ai', which made the
              // backend's `sectionsAdded` metric (it filters on
              // `source === 'manual'`) structurally zero — a false number, not
              // merely an unused one.
              source: s.source,
            })),
          }
        : {
            questions: questions.map((q) => ({
              key: q.key,
              number: q.number,
              text: q.text,
              groupTitle: q.groupTitle,
              wordLimit: q.wordLimit,
            })),
          }

    const itemCount = generation.kind === 'outline' ? sections.length : questions.length
    if (itemCount === 0) {
      set({ error: 'Nothing left to accept — regenerate or discard' })
      return false
    }

    // Blank titles never reach the wire. The UI disables the accept for the same
    // reason; this is the backstop, and it refuses the whole payload rather than
    // dropping the offending row — silently discarding a section the reviewer
    // deliberately added is the quiet loss this programme exists to remove.
    if (generation.kind === 'outline' && sections.some((s) => s.title.trim().length === 0)) {
      set({ error: UNTITLED_SECTION_ERROR })
      return false
    }

    // Guard the outline write with the version the reviewer's outline was loaded
    // at. Add Section stays live during review, so an accept can land on an
    // outline that has moved — a blind full-array write would drop that manual
    // section and GC its requirement links. A stale version 409s instead, and
    // the caller's error surface tells the reviewer to reload.
    //
    // ⚠ There is no unguarded fallback. Sending no version turns the CAS off,
    // and the handler's `status !== 'complete'` check is TOCTOU — two concurrent
    // accepts of one generation would both append. If the drafting store is not
    // on this artifact we have no trustworthy version, so we refuse. Checked
    // before the spinner goes up so a refusal can't strand it.
    const draftingState = useDraftingStore.getState()
    if (draftingState.currentArtifactId !== artifactId) {
      set({ error: MISSING_OUTLINE_VERSION_ERROR })
      return false
    }
    const expectedOutlineVersion = draftingState.outlineVersion

    // The store may be handed to another document while the request is in
    // flight; a stale continuation must not clear or paint over it. It must also
    // report failure: callers act on `true` by reloading the outline for the
    // artifact they captured, which would pull a foreign outline into the store.
    // The spinner is this call's own, so it is always released.
    const epoch = pollEpoch
    set({ isAccepting: true, error: null })

    try {
      const result = await acceptOutlineProposal(
        artifactId,
        generation.id,
        payload,
        expectedOutlineVersion
      )
      if (epoch !== pollEpoch) {
        set({ isAccepting: false })
        return false
      }
      set({
        isAccepting: false,
        generation: null,
        sections: [],
        questions: [],
        isDirty: false,
        // Keep the response rather than discarding it — the same-title warnings
        // are the whole point of C3, and the added count drives the accept
        // confirmation once the proposal rows are gone.
        acceptWarnings: result?.warnings ?? [],
        lastAddedSectionCount: result?.addedSectionCount ?? 0,
      })
      return true
    } catch (err) {
      if (epoch !== pollEpoch) {
        set({ isAccepting: false })
        return false
      }
      set({ isAccepting: false, error: (err as Error).message })
      return false
    }
  },

  discard: async (artifactId) => {
    const { generation, artifactId: currentArtifactId } = get()

    // Cross-artifact guard: discarding is destructive on both sides — it must
    // not send another document's generation id here, nor clear the proposal
    // now on screen.
    if (generation && (generation.artifactId !== artifactId || currentArtifactId !== artifactId)) {
      set({ error: FOREIGN_PROPOSAL_ERROR })
      return
    }

    const epoch = pollEpoch
    if (generation) {
      try {
        await discardOutlineProposal(artifactId, generation.id)
      } catch {
        // Best-effort; clearing local state is what the user sees.
      }
      if (epoch !== pollEpoch) return
    }
    set({
      generation: null,
      sections: [],
      questions: [],
      error: null,
      isDirty: false,
      acceptWarnings: [],
      lastAddedSectionCount: 0,
    })
  },

  reset: () => {
    // Orphan any in-flight poll loop so it can't write into the cleared store.
    pollEpoch++
    set({
      artifactId: null,
      generation: null,
      sections: [],
      questions: [],
      isGenerating: false,
      isAccepting: false,
      error: null,
      isDirty: false,
      acceptWarnings: [],
      lastAddedSectionCount: 0,
    })
  },
}))
